import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';

/**
 * Fixed identity for the pinned group so collapse state never collides with a
 * same-named custom section.
 */
export const PINNED_GROUP_ID = '__pinned__';

/**
 * Splits the nav layout into the pinned group and the remaining sections.
 *
 * The pinned group's display title comes from the routes' own nav metadata
 * (e.g. Inbox declares `group: 'Overview'`), so a section carrying that same
 * title would render a second, identical header — pinned `/chat` titles the
 * group "Assistant", and any unpinned `group: 'Assistant'` route forms its own
 * "Assistant" section. Those items fold into the pinned group instead.
 */
export function buildPinnedGroup(
  pinnedItems: RouterNavEntry[],
  sections: SidebarNavGroup[],
): { pinnedGroup: SidebarNavGroup | null; sections: SidebarNavGroup[] } {
  if (pinnedItems.length === 0) return { pinnedGroup: null, sections };

  const title = pinnedItems[0]?.meta.nav?.group ?? 'Overview';
  const sameTitle = sections.filter((g) => g.title === title);

  return {
    pinnedGroup: {
      id: PINNED_GROUP_ID,
      title,
      items: [...pinnedItems, ...sameTitle.flatMap((g) => g.items)],
    },
    sections: sections.filter((g) => g.title !== title),
  };
}
